import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Image,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useRef, useState } from "react";
import { moneyValue } from "../../lib/money-input";
import { usePermission } from "@how/app-kit";
import {
  ConfirmActionModal,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  useReturnFocus,
} from "@how/ui";
import { mediaUrl } from "../website/media-queries";
import { useAssets } from "../website/website-queries";
import { StoreTabs } from "./StoreTabs";
import {
  FULFILMENT_LABEL,
  formatMoney,
  type ProductFulfilment,
  type ProductRow,
  useDeleteProduct,
  useDeleteVariant,
  useProducts,
  useSaveProduct,
  useSaveVariant,
  type VariantRow,
} from "./store-queries";

function priceRange(p: ProductRow): string {
  const prices = p.variants.filter((v) => v.isActive).map((v) => v.priceCents);
  if (prices.length === 0) return "No price yet";
  const low = Math.min(...prices);
  const high = Math.max(...prices);
  return low === high
    ? formatMoney(low, p.currency)
    : `${formatMoney(low, p.currency)} to ${formatMoney(high, p.currency)}`;
}

function stockSummary(p: ProductRow): string {
  const active = p.variants.filter((v) => v.isActive);
  if (active.length === 0) return "";
  if (active.every((v) => v.stock === null)) return "Not counted";
  const counted = active.filter((v) => v.stock !== null);
  const total = counted.reduce((sum, v) => sum + (v.stock ?? 0), 0);
  return total === 0 && counted.length === active.length ? "Sold out" : `${String(total)} left`;
}

/** Store → Products (ADR 0047): what the church sells in the member portal. */
export function ProductsPage() {
  const canManage = usePermission("commerce.manage_catalog", { module: "commerce", write: true });
  const products = useProducts();
  const remove = useDeleteProduct();
  const [editing, setEditing] = useState<ProductRow | "new" | null>(null);
  // The editor unmounts on close, so focus is handed back to the button that opened it by hand (#261).
  const editorFocus = useReturnFocus();
  const openEditor = (target: ProductRow | "new") => {
    editorFocus.remember();
    setEditing(target);
  };
  const [deleting, setDeleting] = useState<ProductRow | null>(null);

  return (
    <>
      <PageHeader
        title="Store"
        description="What members can buy in the portal, and the orders to hand over or ship."
        actions={
          canManage.data === true ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => {
                openEditor("new");
              }}
            >
              New product
            </Button>
          ) : null
        }
      />
      <StoreTabs active="products" />
      {products.isPending ? (
        <LoadingState rows={4} />
      ) : products.isError ? (
        <ErrorState message={products.error.message} onRetry={() => void products.refetch()} />
      ) : products.data.length === 0 ? (
        <EmptyState
          title="Nothing for sale yet"
          description="Add a product, give it a price, and it appears in the member portal's Shop."
        />
      ) : (
        <Table.ScrollContainer minWidth={640}>
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th>Options</Table.Th>
                <Table.Th className="how-numeric">Price</Table.Th>
                <Table.Th>Stock</Table.Th>
                <Table.Th aria-label="Actions" />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {products.data.map((p) => (
                <Table.Tr key={p.id}>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      {p.imagePath === null ? null : (
                        <Image
                          src={mediaUrl(p.imagePath)}
                          alt={p.imageAlt ?? ""}
                          w={40}
                          h={40}
                          radius="sm"
                          fit="cover"
                        />
                      )}
                      <div>
                        <Group gap="xs">
                          <Text size="sm" fw={500}>
                            {p.name}
                          </Text>
                          {p.isActive ? null : (
                            <Badge variant="light" color="gray">
                              Hidden
                            </Badge>
                          )}
                          {p.isActive && !p.inShop ? (
                            <Badge variant="light" color="gray">
                              Event add-on only
                            </Badge>
                          ) : null}
                        </Group>
                        {p.description === null ? null : (
                          <Text size="xs" c="dimmed" lineClamp={1}>
                            {p.description}
                          </Text>
                        )}
                        {p.fulfilment === "either" ? null : (
                          <Text size="xs" c="dimmed">
                            {FULFILMENT_LABEL[p.fulfilment]}
                          </Text>
                        )}
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {p.variants
                        .filter((v) => v.isActive)
                        .map((v) => v.name)
                        .join(", ") || "None yet"}
                    </Text>
                  </Table.Td>
                  <Table.Td className="how-numeric">{priceRange(p)}</Table.Td>
                  <Table.Td>{stockSummary(p)}</Table.Td>
                  <Table.Td>
                    {canManage.data === true ? (
                      <Group gap="xs" justify="flex-end" wrap="nowrap">
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          aria-label={`Edit ${p.name}`}
                          onClick={() => {
                            openEditor(p);
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          color="red"
                          aria-label={`Remove ${p.name}`}
                          onClick={() => {
                            setDeleting(p);
                          }}
                        >
                          Remove
                        </Button>
                      </Group>
                    ) : null}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      {editing === null ? null : (
        <ProductModal
          product={editing === "new" ? null : editing}
          onClose={() => {
            setEditing(null);
            editorFocus.restore();
          }}
        />
      )}
      <ConfirmActionModal
        opened={deleting !== null}
        onClose={() => {
          setDeleting(null);
        }}
        title="Remove this product"
        message={`Remove ${deleting?.name ?? ""} from the store? Members stop seeing it at once. Orders already placed keep their record.`}
        confirmLabel="Remove"
        danger
        loading={remove.isPending}
        onConfirm={async () => {
          if (deleting !== null) await remove.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </>
  );
}

interface DraftVariant {
  readonly key: string;
  readonly id: string | null;
  readonly name: string;
  /** Empty while the person retypes it. */
  readonly price: number | "";
  /** Empty means "not counted". */
  readonly stock: number | "";
  readonly isActive: boolean;
}

function toDraft(v: VariantRow): DraftVariant {
  return {
    key: v.id,
    id: v.id,
    name: v.name,
    price: v.priceCents / 100,
    stock: v.stock ?? "",
    isActive: v.isActive,
  };
}

function ProductModal({
  product,
  onClose,
}: {
  readonly product: ProductRow | null;
  readonly onClose: () => void;
}) {
  const assets = useAssets();
  const saveProduct = useSaveProduct();
  const saveVariant = useSaveVariant();
  const deleteVariant = useDeleteVariant();
  const [name, setName] = useState(product?.name ?? "");
  const [description, setDescription] = useState(product?.description ?? "");
  const [imageAssetId, setImageAssetId] = useState<string | null>(product?.imageAssetId ?? null);
  const [isActive, setIsActive] = useState(product?.isActive ?? true);
  const [inShop, setInShop] = useState(product?.inShop ?? true);
  const [fulfilment, setFulfilment] = useState<ProductFulfilment>(product?.fulfilment ?? "either");
  const [variants, setVariants] = useState<DraftVariant[]>(
    product === null || product.variants.length === 0
      ? [{ key: "new-0", id: null, name: "Standard", price: 10, stock: "", isActive: true }]
      : product.variants.map(toDraft),
  );
  const [removed, setRemoved] = useState<string[]>([]);
  // A new row takes focus in its name, and a removed row gives focus to "Add an option" (#261).
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const addOptionRef = useRef<HTMLButtonElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const update = (key: string, patch: Partial<DraftVariant>) => {
    setVariants((current) => current.map((v) => (v.key === key ? { ...v, ...patch } : v)));
  };
  const valid =
    name.trim() !== "" &&
    variants.length > 0 &&
    variants.every((v) => v.name.trim() !== "" && v.price !== "" && v.price >= 0.5);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const productId = await saveProduct.mutateAsync({
        productId: product?.id ?? null,
        name: name.trim(),
        description,
        imageAssetId,
        isActive,
        fulfilment,
        inShop,
      });
      for (const id of removed) await deleteVariant.mutateAsync(id);
      for (const v of variants) {
        await saveVariant.mutateAsync({
          productId,
          variantId: v.id,
          name: v.name.trim(),
          priceCents: Math.round((v.price === "" ? 0 : v.price) * 100),
          stock: v.stock === "" ? null : v.stock,
          isActive: v.isActive,
        });
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "The product was not saved.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={product === null ? "New product" : "Edit product"}
      size="lg"
    >
      <Stack gap="md">
        <TextInput
          label="Name"
          required
          maxLength={120}
          value={name}
          onChange={(event) => {
            setName(event.currentTarget.value);
          }}
        />
        <Textarea
          label="Description"
          autosize
          minRows={2}
          maxLength={2000}
          value={description}
          onChange={(event) => {
            setDescription(event.currentTarget.value);
          }}
        />
        <Select
          label="Image"
          description="From the church's media library, with its description for people who cannot see it."
          placeholder={assets.isError ? "The media library is not open to you" : "No image"}
          clearable
          disabled={assets.isError}
          data={(assets.data ?? []).map((a) => ({ value: a.id, label: a.alt_text }))}
          value={imageAssetId}
          onChange={setImageAssetId}
        />
        <Select
          label="How members get it"
          description="Either follows the store's settings; choose one for a meal ticket or a print that is only mailed."
          allowDeselect={false}
          data={(Object.keys(FULFILMENT_LABEL) as ProductFulfilment[]).map((value) => ({
            value,
            label: FULFILMENT_LABEL[value],
          }))}
          value={fulfilment}
          onChange={(value) => {
            if (value !== null) setFulfilment(value as ProductFulfilment);
          }}
        />
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Options
          </Text>
          <Text size="xs" c="dimmed">
            One row per size, colour or kind, each with its own price. Leave stock empty when you do
            not count it; members see "3 left" when fewer than ten remain, and "sold out" at none.
          </Text>
          {variants.map((v, n) => {
            // Each row's controls say which option they belong to; a new one has no name yet (#261).
            const row = v.name.trim() === "" ? `option ${String(n + 1)}` : v.name.trim();
            return (
              <Group key={v.key} gap="xs" align="flex-end" wrap="nowrap">
                <TextInput
                  label="Option"
                  aria-label={`Option ${String(n + 1)}`}
                  ref={
                    v.key === focusKey
                      ? (element: HTMLInputElement | null) => {
                          if (element === null) return;
                          element.focus();
                          setFocusKey(null);
                        }
                      : undefined
                  }
                  value={v.name}
                  maxLength={80}
                  style={{ flex: 2 }}
                  onChange={(event) => {
                    update(v.key, { name: event.currentTarget.value });
                  }}
                />
                <NumberInput
                  label="Price"
                  aria-label={`Price of ${row}`}
                  prefix="$"
                  decimalScale={2}
                  fixedDecimalScale
                  min={0.5}
                  value={v.price}
                  style={{ flex: 1 }}
                  onChange={(value) => {
                    update(v.key, { price: moneyValue(value) });
                  }}
                />
                <NumberInput
                  label="Stock"
                  aria-label={`Stock of ${row}`}
                  placeholder="Not counted"
                  min={0}
                  allowDecimal={false}
                  value={v.stock}
                  style={{ flex: 1 }}
                  onChange={(value) => {
                    update(v.key, { stock: typeof value === "number" ? value : "" });
                  }}
                />
                <Switch
                  label="On sale"
                  aria-label={`On sale: ${row}`}
                  checked={v.isActive}
                  mb={8}
                  onChange={(event) => {
                    update(v.key, { isActive: event.currentTarget.checked });
                  }}
                />
                <ActionIcon
                  variant="subtle"
                  color="red"
                  mb={4}
                  aria-label={`Remove ${row}`}
                  disabled={variants.length === 1}
                  onClick={() => {
                    if (v.id !== null) setRemoved((r) => [...r, v.id as string]);
                    setVariants((current) => current.filter((x) => x.key !== v.key));
                    addOptionRef.current?.focus();
                  }}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            );
          })}
          <Group>
            <Button
              ref={addOptionRef}
              size="compact-sm"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={() => {
                const key = `new-${String(Date.now())}`;
                setFocusKey(key);
                setVariants((current) => [
                  ...current,
                  {
                    key,
                    id: null,
                    name: "",
                    price: current.at(-1)?.price || 10,
                    stock: "",
                    isActive: true,
                  },
                ]);
              }}
            >
              Add an option
            </Button>
          </Group>
        </Stack>
        <Switch
          label="On sale"
          description="Off hides it everywhere: the Shop and any event that offers it."
          checked={isActive}
          onChange={(event) => {
            setIsActive(event.currentTarget.checked);
          }}
        />
        <Switch
          label="Sold in the member portal's Shop"
          description="Off for something only offered with an event, such as a meal. Choose an event's add-ons from the event."
          checked={inShop}
          disabled={!isActive}
          onChange={(event) => {
            setInShop(event.currentTarget.checked);
          }}
        />
        {error === null ? null : (
          <Alert color="red" variant="light">
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saving} disabled={!valid} onClick={() => void save()}>
            Save product
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
